import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowRight, ArrowUpRight, Brain, FileText, Sparkles, ShieldCheck,
  Activity, Workflow, Database, Cpu, Network, Radio, LineChart,
  AlertTriangle, CheckCircle2, X, Loader2, Plus, Minus, Building2,
  Wallet, Wrench, Users, Globe, Lock, Bot, Gauge, Boxes,
} from 'lucide-react';
import { api } from '@/utils/api';
import RevaLogo from '@/components/RevaLogo';
import CinematicCity from '@/components/CinematicCity';

import T from '@/components/landing/copy';
import { tt, Section, Eyebrow, HeroOrbField } from '@/components/landing/atoms';
import Hero from '@/components/landing/Hero';
import OperationsModules from '@/components/landing/OperationsModules';
import TopNavMega from '@/components/landing/TopNavMega';
import OperationalIntelligence from '@/components/landing/OperationalIntelligence';
import { DemoRequestModal, TourModal } from '@/components/landing/Modals';



// ─────────────────────────────────────────────────────────────────────────────
// SECTIONS — every button does something real
// ─────────────────────────────────────────────────────────────────────────────
function TopNav({ lang, setLang, onDemo, onTour, navigate }) {
  const NAV = [
    { id: 'platform',     k: T.nav.platform },
    { id: 'intelligence', k: T.nav.intelligence },
    { id: 'modules',      k: T.nav.modules },
    { id: 'security',     k: T.nav.security },
    { id: 'faq',          k: T.nav.faq },
  ];
  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl border-b border-[#E6EAF0] bg-[rgba(255,255,255,0.82)]" data-testid="reva-topnav">
      <div className="max-w-[1240px] mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
        <button onClick={onTour} className="flex items-center" data-testid="reva-nav-logo" aria-label="REVA">
          <RevaLogo variant="full" size={28} theme="light" />
        </button>
        <nav className="hidden md:flex items-center gap-8" aria-label="Primary">
          {NAV.map(n => (
            <a key={n.id} href={`#${n.id}`} className="text-[13px] text-[#4A5263] hover:text-[#0E1320] transition-colors" data-testid={`reva-nav-${n.id}`}>
              {tt(n.k, lang)}
            </a>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setLang(lang === 'ar' ? 'en' : 'ar')}
            className="text-[11px] font-bold tracking-[0.18em] text-[#4A5263] hover:text-[#0E1320] px-2.5 py-1.5 rounded-full transition-colors reva-mono"
            data-testid="reva-nav-lang"
            aria-label="Toggle language"
          >
            <Globe className="h-3 w-3 inline me-1.5" />
            {lang === 'ar' ? 'EN' : 'AR'}
          </button>
          <button onClick={() => navigate('/auth')} className="hidden sm:inline-flex text-[13px] font-medium text-[#2A3340] hover:text-[#0E1320] px-3 py-1.5 rounded-full transition-colors" data-testid="reva-nav-signin">
            {tt(T.nav.signIn, lang)}
          </button>
          <button onClick={onDemo} className="reva-btn-primary text-[13px] py-2 px-4" data-testid="reva-nav-demo">
            {tt(T.nav.requestAccess, lang)} <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </header>
  );
}

function OperatorMarquee({ lang }) {
  const icons = [Activity, Bot, Database, Radio, ShieldCheck, LineChart, Network, Workflow];
  const doubled = [...T.marquee, ...T.marquee];
  return (
    <Section className="py-10">
      <div className="reva-glass px-3 py-3 overflow-hidden">
        <div className="flex gap-10 reva-marquee whitespace-nowrap">
          {doubled.map((m, i) => {
            const Icon = icons[i % icons.length];
            return (
              <div key={i} className="flex items-center gap-2.5 text-[12px] text-white/70 reva-mono shrink-0">
                <Icon className="h-3.5 w-3.5 text-[color:var(--reva-signal)]" strokeWidth={1.6} />
                {tt(m, lang)}
              </div>
            );
          })}
        </div>
      </div>
    </Section>
  );
}

// ── Live Operational Pulse Bar ─────────────────────────────────────────────
// Shows real-time operational telemetry across the portfolio. Each tile
// communicates "this platform is alive, watching, calculating, recommending."
function LivePulseBar({ lang }) {
  const toneMap = {
    signal: { text: 'var(--reva-signal-2)', glow: 'rgba(0,168,107,0.25)' },
    mint:   { text: 'var(--reva-mint)',     glow: 'rgba(31,209,138,0.22)' },
    amber:  { text: 'var(--reva-amber)',    glow: 'rgba(255,181,71,0.22)' },
    coral:  { text: 'var(--reva-coral)',    glow: 'rgba(255,106,85,0.22)' },
  };
  return (
    <Section className="pt-2 pb-6">
      <div className="flex items-center gap-2 mb-3 px-1">
        <span className="inline-flex items-center gap-2 text-[10.5px] tracking-[0.20em] reva-mono text-white/70 uppercase">
          <span className="relative inline-flex">
            <span className="w-2 h-2 rounded-full bg-[color:var(--reva-signal-2)]" />
            <span className="absolute inset-0 w-2 h-2 rounded-full bg-[color:var(--reva-signal)] reva-pulse" />
          </span>
          {tt(T.pulse.eyebrow, lang)}
        </span>
        <span className="ms-auto text-[10px] reva-mono text-white/40">{new Date().toLocaleString('en-US', { month: 'short', day: 'numeric' }).toUpperCase()}</span>
      </div>
      <div className="reva-glass-2 p-2 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none opacity-60">
          <div className="reva-drift-line" style={{ top: '72%', animationDelay: '3.4s' }} />
        </div>
        <div className="relative grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-white/[0.04]">
          {T.pulse.metrics.map((m, i) => {
            const c = toneMap[m.tone] || toneMap.signal;
            return (
              <div key={i} className="bg-[#101317] p-4 relative group" data-testid={`reva-pulse-${i}`}>
                <div className="absolute -top-8 -right-8 w-24 h-24 rounded-full opacity-50 group-hover:opacity-100 transition-opacity pointer-events-none" style={{ background: `radial-gradient(closest-side, ${c.glow}, transparent 70%)` }} />
                <div className="text-[10px] tracking-[0.16em] reva-mono text-white/55 uppercase truncate">{tt(m.k, lang)}</div>
                <div className="mt-1.5 text-[26px] leading-none reva-mono text-white tabular-nums">{m.v}</div>
                <div className="mt-1 text-[10.5px] reva-mono" style={{ color: c.text }}>{tt(m.d, lang)}</div>
              </div>
            );
          })}
        </div>
      </div>
    </Section>
  );
}


function ProblemSplit({ lang }) {
  return (
    <Section id="platform" className="py-24">
      <Eyebrow>{tt(T.problem.eyebrow, lang)}</Eyebrow>
      <h2 className="mt-5 reva-display text-3xl sm:text-5xl max-w-[20ch]">
        {tt(T.problem.titleA, lang)} <span className="reva-signal-text">{tt(T.problem.titleB, lang)}</span>
      </h2>
      <div className="mt-12 grid lg:grid-cols-2 gap-5">
        <div className="reva-glass p-7">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-white/55 reva-mono">
            <X className="h-3.5 w-3.5" /> {tt(T.problem.oldHeader, lang)}
          </div>
          <ul className="mt-5 space-y-3.5">
            {T.problem.old.map((t, i) => (
              <li key={i} className="flex gap-3 text-[14px] text-white/70"><span className="mt-2 h-px w-6 bg-white/20 shrink-0" /><span>{tt(t, lang)}</span></li>
            ))}
          </ul>
        </div>
        <div className="reva-glass-2 p-7 relative overflow-hidden">
          <div className="absolute -top-12 -right-12 w-56 h-56 rounded-full pointer-events-none" style={{ background: 'radial-gradient(closest-side, rgba(0,168,107,0.30), transparent 70%)' }} />
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] reva-mono text-[color:var(--reva-signal)]">
            <Sparkles className="h-3.5 w-3.5" /> {tt(T.problem.newHeader, lang)}
          </div>
          <ul className="mt-5 space-y-3.5">
            {T.problem.new.map((t, i) => (
              <li key={i} className="flex gap-3 text-[14px] text-white"><CheckCircle2 className="h-4 w-4 mt-0.5 text-[color:var(--reva-signal)] shrink-0" strokeWidth={1.6} /><span>{tt(t, lang)}</span></li>
            ))}
          </ul>
        </div>
      </div>
    </Section>
  );
}

function IntelligenceLayers({ lang }) {
  return (
    <Section id="intelligence" className="py-24">
      <div className="grid lg:grid-cols-12 gap-10 items-start">
        <div className="lg:col-span-5">
          <Eyebrow>{tt(T.layers.eyebrow, lang)}</Eyebrow>
          <h2 className="mt-5 reva-display text-3xl sm:text-5xl">{tt(T.layers.titleA, lang)}<br />{tt(T.layers.titleB, lang)}</h2>
          <p className="mt-6 text-white/75 max-w-md leading-relaxed">{tt(T.layers.sub, lang)}</p>
        </div>
        <div className="lg:col-span-7 space-y-3">
          {T.layers.items.map((l, i) => {
            const Icon = l.icon;
            return (
              <div key={i} className="reva-glass p-6 hover:border-white/20 transition-colors">
                <div className="flex items-start gap-5">
                  <div className="text-[40px] leading-none reva-mono text-white/20 font-light">{l.n}</div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2.5">
                      <Icon className="h-4 w-4 text-[color:var(--reva-signal)]" strokeWidth={1.6} />
                      <span className="text-[10px] tracking-[0.18em] reva-mono text-white/55">{tt(l.tag, lang)}</span>
                    </div>
                    <h3 className="mt-2 text-xl font-medium text-white">{tt(l.title, lang)}</h3>
                    <p className="mt-1.5 text-[14px] text-white/70 max-w-xl">{tt(l.desc, lang)}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </Section>
  );
}

function AIWorkbench({ lang, navigate }) {
  const rows = [
    ['اسم العقار',          'property_name',    98],
    ['Annual Rent (SAR)',    'annual_rent',      99],
    ['# of beds',            'bedrooms',         96],
    ['Tenant CR',            'national_id',      88],
    ['Move-in',              'lease.start_date', 94],
    ['Status (مشغول/شاغر)', 'unit.status',      92],
  ];
  return (
    <Section className="py-24">
      <div className="grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-5">
          <Eyebrow>{tt(T.workbench.eyebrow, lang)}</Eyebrow>
          <h2 className="mt-5 reva-display text-3xl sm:text-5xl">{tt(T.workbench.titleA, lang)}<br />{tt(T.workbench.titleB, lang)}</h2>
          <p className="mt-6 text-white/75 leading-relaxed max-w-md">{tt(T.workbench.sub, lang)}</p>
          <div className="mt-7 flex gap-2 flex-wrap">
            {T.workbench.chips.map((t, i) => (
              <span key={i} className="text-[11px] reva-mono text-white/75 px-2.5 py-1 rounded-full border border-white/15">{tt(t, lang)}</span>
            ))}
          </div>
          <button onClick={() => navigate('/excel-import')} className="reva-btn-ghost mt-6 text-[13px]" data-testid="reva-workbench-cta">
            {tt({en:'Open AI workbench', ar:'افتح منصة الذكاء'}, lang)} <ArrowUpRight className="h-3.5 w-3.5" />
          </button>
        </div>
        <div className="lg:col-span-7">
          <div className="reva-glass-2 p-1.5">
            <div className="px-4 py-2.5 flex items-center gap-2 border-b border-white/[0.08]">
              <span className="w-2.5 h-2.5 rounded-full bg-white/20" />
              <span className="w-2.5 h-2.5 rounded-full bg-white/20" />
              <span className="w-2.5 h-2.5 rounded-full bg-white/20" />
              <span className="ms-3 text-[11px] reva-mono text-white/55">workbench · column-mapper.tsx</span>
              <span className="ms-auto inline-flex items-center gap-1.5 text-[10px] reva-mono text-[color:var(--reva-mint)]">
                <span className="w-1.5 h-1.5 rounded-full bg-[color:var(--reva-mint)] reva-pulse" /> LIVE
              </span>
            </div>
            <div className="p-5">
              <div className="grid grid-cols-12 gap-4 text-[11px] uppercase tracking-[0.16em] reva-mono text-white/55 pb-2 border-b border-white/[0.06]">
                <div className="col-span-5">{tt(T.workbench.cols.raw, lang)}</div>
                <div className="col-span-5">{tt(T.workbench.cols.sug, lang)}</div>
                <div className="col-span-2 text-end">{tt(T.workbench.cols.conf, lang)}</div>
              </div>
              {rows.map(([raw, sug, conf], i) => (
                <div key={i} className="grid grid-cols-12 gap-4 py-3 border-b border-white/[0.05] items-center" style={{ animation: 'reva-row-flow 6s ease-in-out infinite', animationDelay: `${i * 0.6}s` }}>
                  <div className="col-span-5 text-[13px] text-white/75 truncate" dir="auto">{raw}</div>
                  <div className="col-span-5 text-[13px] reva-mono text-[color:var(--reva-signal)] truncate">{sug}</div>
                  <div className="col-span-2 text-end text-[12px] reva-mono text-white">{conf}%</div>
                </div>
              ))}
              <div className="pt-4 flex items-center justify-between">
                <div className="text-[11px] reva-mono text-white/55">{tt(T.workbench.footer, lang)}</div>
                <button onClick={() => navigate('/excel-import')} className="reva-btn text-[12px] py-1.5" data-testid="reva-workbench-import">
                  {tt(T.workbench.cta, lang)} <ArrowRight className="h-3 w-3" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Section>
  );
}


// ── Operational Intelligence — Interactive Command Center Preview ─────────
// The centerpiece "data intelligence" section. Shows REVA actively thinking:
// NOI sparkline, occupancy heat strip, live signal stream, AI recommendations,
// anomaly detection — all wired together to feel like a real ops cockpit.

function DecisionEngine({ lang, navigate }) {
  const colorMap = {
    mint:   { tag: 'var(--reva-mint)',   glow: 'rgba(31,209,138,0.28)' },
    signal: { tag: 'var(--reva-signal)', glow: 'rgba(0,168,107,0.28)' },
    amber:  { tag: 'var(--reva-amber)',  glow: 'rgba(255,181,71,0.22)' },
  };
  return (
    <Section id="modules" className="py-24">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <Eyebrow>{tt(T.decision.eyebrow, lang)}</Eyebrow>
          <h2 className="mt-5 reva-display text-3xl sm:text-5xl max-w-[20ch]">
            {tt(T.decision.titleA, lang)} <span className="reva-signal-text">{tt(T.decision.titleB, lang)}</span>{tt(T.decision.titleC, lang)}
          </h2>
        </div>
        <p className="text-white/75 max-w-md text-[14px]">{tt(T.decision.sub, lang)}</p>
      </div>
      <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {T.decision.tiles.map((t, i) => {
          const Icon = t.icon;
          const c = colorMap[t.color];
          return (
            <button
              key={i}
              onClick={() => navigate(t.route)}
              className="text-start reva-glass p-6 hover:-translate-y-0.5 transition-transform relative overflow-hidden group"
              data-testid={`reva-module-${i}`}
            >
              <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full pointer-events-none opacity-60 group-hover:opacity-100 transition-opacity" style={{ background: `radial-gradient(closest-side, ${c.glow}, transparent 70%)` }} />
              <div className="relative flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.10)' }}>
                  <Icon className="h-4 w-4" style={{ color: c.tag }} strokeWidth={1.6} />
                </div>
                <span className="text-[10px] tracking-[0.18em] reva-mono text-white/55">MODULE / 0{i + 1}</span>
              </div>
              <h3 className="mt-5 text-lg font-medium text-white">{tt(t.title, lang)}</h3>
              <p className="mt-1.5 text-[13px] text-white/70 leading-relaxed">{tt(t.desc, lang)}</p>
              <div className="mt-5 inline-flex items-center gap-1.5 text-[12px] reva-mono" style={{ color: c.tag }}>
                {tt(T.decision.cta, lang)} <ArrowUpRight className="h-3.5 w-3.5" />
              </div>
            </button>
          );
        })}
      </div>
    </Section>
  );
}

function LeaseAbstraction({ lang, navigate }) {
  const fields = [
    ['tenant.full_name',    'Faisal Al-Subaiei'],
    ['lease.start_date',    '2026-02-01'],
    ['lease.end_date',      '2027-01-31'],
    ['lease.annual_rent',   '186,000 SAR'],
    ['payment.frequency',   'quarterly'],
    ['security_deposit',    '15,500 SAR'],
    ['indemnity_clause',    'standard · §12.4'],
    ['auto_renew',          'true'],
  ];
  return (
    <Section className="py-24">
      <div className="grid lg:grid-cols-12 gap-10 items-start">
        <div className="lg:col-span-5">
          <Eyebrow>{tt(T.lease.eyebrow, lang)}</Eyebrow>
          <h2 className="mt-5 reva-display text-3xl sm:text-5xl">{tt(T.lease.titleA, lang)}<br />{tt(T.lease.titleB, lang)}</h2>
          <p className="mt-6 text-white/75 leading-relaxed max-w-md">{tt(T.lease.sub, lang)}</p>
          <button onClick={() => navigate('/lease-abstract')} className="reva-btn-ghost mt-6 text-[13px]" data-testid="reva-lease-cta">
            {tt(T.lease.cta, lang)} <ArrowUpRight className="h-3.5 w-3.5" />
          </button>
        </div>
        <div className="lg:col-span-7">
          <div className="grid sm:grid-cols-12 gap-4">
            <div className="sm:col-span-5 reva-glass p-5 relative overflow-hidden h-full">
              <div className="absolute inset-0 reva-grid-bg opacity-40" />
              <div className="relative">
                <div className="flex items-center gap-1.5 text-[10px] reva-mono text-white/55"><FileText className="h-3 w-3" /> LEASE_2026_FAS.pdf</div>
                <div className="mt-4 space-y-2">
                  {Array.from({ length: 14 }).map((_, i) => (
                    <div key={i} className="h-1.5 rounded-full bg-white/[0.10]" style={{ width: `${60 + (i * 7) % 40}%` }} />
                  ))}
                </div>
                <div className="mt-3 inline-flex items-center gap-1.5 text-[10px] reva-mono text-[color:var(--reva-signal)]">
                  <Cpu className="h-3 w-3" /> {tt(T.lease.extracting, lang)}
                </div>
              </div>
            </div>
            <div className="sm:col-span-7 reva-glass-2 p-5">
              <div className="flex items-center justify-between text-[10px] reva-mono text-white/55"><span>extracted.json</span><span className="text-[color:var(--reva-mint)]">200 OK · 8.1s</span></div>
              <div className="mt-4 space-y-1.5">
                {fields.map(([k, v], i) => (
                  <div key={i} className="flex items-baseline gap-3 text-[12.5px] reva-mono">
                    <span className="text-white/55 truncate min-w-[160px]">{k}</span>
                    <span className="text-white/20">:</span>
                    <span className="text-[color:var(--reva-signal)] truncate">&quot;{v}&quot;</span>
                  </div>
                ))}
              </div>
              <div className="mt-4 pt-3 border-t border-white/[0.08] text-[10px] reva-mono text-white/55">{tt(T.lease.footer, lang)}</div>
            </div>
          </div>
        </div>
      </div>
    </Section>
  );
}

function PredictiveIntelligence({ lang }) {
  const W = 720, H = 220, P = 16;
  const points = [62, 64, 63, 65, 66, 68, 70, 71, 73, 76, 78, 81, 84, 86, 88, 90];
  const x = (i) => P + (i * (W - 2 * P)) / (points.length - 1);
  const y = (v) => H - P - ((v - 50) * (H - 2 * P)) / 50;
  const path  = points.map((v, i) => `${i ? 'L' : 'M'} ${x(i)} ${y(v)}`).join(' ');
  const upper = points.map((v) => v + 4).map((v, i) => `${i ? 'L' : 'M'} ${x(i)} ${y(v)}`).join(' ');
  const lower = points.map((v) => v - 5).map((v, i) => `${i ? 'L' : 'M'} ${x(i)} ${y(v)}`).reverse().join(' ');
  const icons = [AlertTriangle, Activity, Sparkles];
  const colors = ['var(--reva-amber)', 'var(--reva-signal)', 'var(--reva-mint)'];
  return (
    <Section className="py-24">
      <Eyebrow>{tt(T.predict.eyebrow, lang)}</Eyebrow>
      <h2 className="mt-5 reva-display text-3xl sm:text-5xl max-w-[20ch]">
        {tt(T.predict.titleA, lang)} <span className="reva-signal-text">{tt(T.predict.titleB, lang)}</span>
      </h2>
      <div className="mt-12 grid lg:grid-cols-12 gap-5">
        <div className="lg:col-span-8 reva-glass-2 p-6">
          <div className="flex items-center justify-between text-[11px] reva-mono text-white/55">
            <span>{tt(T.predict.chartTitle, lang)}</span>
            <span className="text-[color:var(--reva-signal)]">{tt(T.predict.chartConf, lang)}</span>
          </div>
          <svg viewBox={`0 0 ${W} ${H}`} className="w-full mt-4 overflow-visible">
            <defs>
              <linearGradient id="rg-fill" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor="#1FD18A" stopOpacity="0.4" />
                <stop offset="100%" stopColor="#1FD18A" stopOpacity="0" />
              </linearGradient>
            </defs>
            {Array.from({ length: 5 }).map((_, i) => (
              <line key={i} x1={P} x2={W - P} y1={P + i * ((H - 2 * P) / 4)} y2={P + i * ((H - 2 * P) / 4)} stroke="rgba(255,255,255,0.08)" />
            ))}
            <path d={`${upper} ${lower} Z`} fill="url(#rg-fill)" />
            <path d={path} fill="none" stroke="#1FD18A" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
            <circle cx={x(points.length - 1)} cy={y(points[points.length - 1])} r="5" fill="#1FD18A" />
            <circle cx={x(points.length - 1)} cy={y(points[points.length - 1])} r="9" fill="#1FD18A" opacity="0.18" className="reva-pulse" />
          </svg>
        </div>
        <div className="lg:col-span-4 space-y-3">
          {T.predict.cards.map((m, i) => {
            const Icon = icons[i];
            return (
              <div key={i} className="reva-glass p-4">
                <div className="flex items-center gap-2 text-[11px] reva-mono text-white/55"><Icon className="h-3.5 w-3.5" style={{ color: colors[i] }} /> {tt(m.label, lang)}</div>
                <div className="mt-2 text-3xl reva-mono text-white">{m.value}</div>
                <div className="text-[11px] reva-mono mt-0.5" style={{ color: colors[i] }}>{tt(m.delta, lang)}</div>
              </div>
            );
          })}
        </div>
      </div>
    </Section>
  );
}

function ExecutiveCenter({ lang, navigate }) {
  return (
    <Section className="py-24">
      <div className="grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7 reva-glass-2 p-6 relative overflow-hidden">
          <div className="absolute inset-0 reva-grid-bg opacity-30 pointer-events-none" />
          <div className="relative">
            <div className="flex items-center justify-between text-[11px] reva-mono text-white/55">
              <span>{tt(T.exec.reportTitle, lang)}</span>
              <span className="text-[color:var(--reva-mint)]">{tt(T.exec.drafted, lang)}</span>
            </div>
            <div className="mt-5 grid grid-cols-3 gap-3">
              {[['NOI', '8.42M SAR', '+11.2%'], ['Occupancy', '92.4%', '+1.8 pp'], ['DSCR', '1.74×', 'stable']].map(([k, v, d], i) => (
                <div key={i} className="rounded-xl border border-white/[0.08] bg-white/[0.03] p-4">
                  <div className="text-[10px] reva-mono uppercase tracking-[0.16em] text-white/55">{k}</div>
                  <div className="mt-1.5 text-2xl reva-mono text-white">{v}</div>
                  <div className="text-[11px] mt-0.5 reva-mono text-[color:var(--reva-mint)]">{d}</div>
                </div>
              ))}
            </div>
            <div className="mt-5 space-y-2.5">
              {T.exec.bullets.map((t, i) => (
                <div key={i} className="flex gap-3 text-[13px] text-white/85"><span className="mt-2 h-px w-6 bg-[color:var(--reva-signal)]" /><span>{tt(t, lang)}</span></div>
              ))}
            </div>
          </div>
        </div>
        <div className="lg:col-span-5">
          <Eyebrow>{tt(T.exec.eyebrow, lang)}</Eyebrow>
          <h2 className="mt-5 reva-display text-3xl sm:text-5xl">{tt(T.exec.titleA, lang)}<br />{tt(T.exec.titleB, lang)}</h2>
          <p className="mt-6 text-white/75 leading-relaxed max-w-md">{tt(T.exec.sub, lang)}</p>
          <div className="mt-6 flex gap-2 flex-wrap">
            {T.exec.chips.map((t, i) => (
              <span key={i} className="text-[11px] reva-mono text-white/75 px-2.5 py-1 rounded-full border border-white/15">{tt(t, lang)}</span>
            ))}
          </div>
          <button onClick={() => navigate('/ai-insights')} className="reva-btn-ghost mt-6 text-[13px]" data-testid="reva-exec-cta">
            {tt(T.exec.cta, lang)} <ArrowUpRight className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </Section>
  );
}

function TenantOps({ lang }) {
  return (
    <Section className="py-24">
      <Eyebrow>{tt(T.tenant.eyebrow, lang)}</Eyebrow>
      <h2 className="mt-5 reva-display text-3xl sm:text-5xl max-w-[20ch]">
        {tt(T.tenant.titleA, lang)} <span className="reva-signal-text">{tt(T.tenant.titleB, lang)}</span>
      </h2>
      <div className="mt-12 grid lg:grid-cols-3 gap-4">
        {T.tenant.items.map((m, i) => {
          const I = m.icon;
          return (
            <div key={i} className="reva-glass p-6">
              <I className="h-5 w-5 text-[color:var(--reva-signal)]" strokeWidth={1.5} />
              <h3 className="mt-4 text-lg font-medium text-white">{tt(m.title, lang)}</h3>
              <p className="mt-1.5 text-[13px] text-white/70 leading-relaxed">{tt(m.desc, lang)}</p>
            </div>
          );
        })}
      </div>
    </Section>
  );
}

function VendorMesh({ lang, navigate }) {
  const nums = ['98', '143', '87', '212', '54', '68'];
  const stars = ['4.7★', '4.6★', '4.8★', '4.5★', '4.9★', '4.6★'];
  return (
    <Section className="py-24">
      <div className="grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-5">
          <Eyebrow>{tt(T.vendor.eyebrow, lang)}</Eyebrow>
          <h2 className="mt-5 reva-display text-3xl sm:text-5xl">{tt(T.vendor.titleA, lang)}<br />{tt(T.vendor.titleB, lang)}</h2>
          <p className="mt-6 text-white/75 leading-relaxed max-w-md">{tt(T.vendor.sub, lang)}</p>
          <button onClick={() => navigate('/marketplace')} className="reva-btn-ghost mt-6 text-[13px]" data-testid="reva-vendor-cta">
            {tt({en:'Open vendor mesh', ar:'افتح شبكة المزوّدين'}, lang)} <ArrowUpRight className="h-3.5 w-3.5" />
          </button>
        </div>
        <div className="lg:col-span-7">
          <div className="reva-glass-2 p-6">
            <div className="grid grid-cols-3 gap-3">
              {T.vendor.categories.map((cat, i) => (
                <div key={i} className="rounded-xl border border-white/[0.08] bg-white/[0.03] p-3.5">
                  <div className="text-[11px] reva-mono text-white/55 uppercase tracking-wider">{tt(cat, lang)}</div>
                  <div className="mt-1.5 text-base reva-mono text-white">{nums[i]}</div>
                  <div className="text-[11px] reva-mono text-[color:var(--reva-amber)]">{stars[i]}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </Section>
  );
}

function Security({ lang }) {
  return (
    <Section id="security" className="py-24">
      <Eyebrow>{tt(T.security.eyebrow, lang)}</Eyebrow>
      <h2 className="mt-5 reva-display text-3xl sm:text-5xl">{tt(T.security.title, lang)}</h2>
      <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {T.security.items.map((m, i) => {
          const I = m.icon;
          return (
            <div key={i} className="reva-glass p-5">
              <I className="h-5 w-5 text-[color:var(--reva-signal)]" strokeWidth={1.5} />
              <div className="mt-4 text-base text-white font-medium">{tt(m.title, lang)}</div>
              <div className="mt-1 text-[13px] text-white/70 leading-relaxed">{tt(m.desc, lang)}</div>
            </div>
          );
        })}
      </div>
    </Section>
  );
}

function ArchitectureDiagram({ lang }) {
  return (
    <Section id="architecture" className="py-24">
      <Eyebrow>{tt(T.arch.eyebrow, lang)}</Eyebrow>
      <h2 className="mt-5 reva-display text-3xl sm:text-5xl max-w-[18ch]">{tt(T.arch.title, lang)}</h2>
      <div className="mt-12 reva-glass-2 p-8 relative overflow-hidden">
        <div className="absolute inset-0 reva-grid-bg opacity-40 pointer-events-none" />
        <div className="relative space-y-6">
          {T.arch.rows.map((row, i) => (
            <div key={i} className="flex items-center gap-4">
              <div className="w-44 text-[11px] reva-mono uppercase tracking-[0.16em] text-white/55">{tt(row.title, lang)}</div>
              <div className="flex-1 h-12 rounded-xl border border-white/[0.10] bg-white/[0.03] flex items-center px-4 text-[13px] reva-mono text-white/85 relative overflow-hidden">
                <div className="absolute inset-y-0 start-0 w-1 bg-[color:var(--reva-signal)] opacity-50" />
                {tt(row.tags, lang)}
              </div>
            </div>
          ))}
        </div>
      </div>
    </Section>
  );
}

function TrustedBy({ lang }) {
  const labels = ['ARQAM', 'NORTHWIND', 'OLAYA HOLDINGS', 'REDSEA · MGT', 'RIYADH STATIC', 'AL RAWABI', 'TANBI', 'NEOM-CO'];
  const doubled = [...labels, ...labels];
  return (
    <Section className="py-16">
      <div className="text-center text-[11px] tracking-[0.2em] reva-mono text-white/55">{tt(T.trusted.title, lang)}</div>
      <div className="mt-8 reva-glass overflow-hidden">
        <div className="flex gap-16 reva-marquee py-5 whitespace-nowrap">
          {doubled.map((l, i) => <span key={i} className="text-[15px] tracking-[0.18em] reva-mono text-white/55 shrink-0">{l}</span>)}
        </div>
      </div>
    </Section>
  );
}

function Quotes({ lang }) {
  return (
    <Section className="py-24">
      <div className="grid md:grid-cols-3 gap-4">
        {T.quotes.map((qq, i) => (
          <figure key={i} className="reva-glass p-6">
            <blockquote className="text-[16px] leading-relaxed text-white/90">&quot;{tt(qq.q, lang)}&quot;</blockquote>
            <figcaption className="mt-5 text-[11px] reva-mono uppercase tracking-[0.16em] text-white/55">{tt(qq.a, lang)}</figcaption>
          </figure>
        ))}
      </div>
    </Section>
  );
}

function Compare({ lang }) {
  return (
    <Section className="py-24">
      <Eyebrow>{tt(T.compare.eyebrow, lang)}</Eyebrow>
      <h2 className="mt-5 reva-display text-3xl sm:text-5xl max-w-[18ch]">{tt(T.compare.title, lang)}</h2>
      <div className="mt-12 reva-glass-2 overflow-hidden">
        <div className="grid grid-cols-12 px-6 py-3 text-[11px] reva-mono text-white/55 uppercase tracking-[0.16em] border-b border-white/[0.08]">
          <div className="col-span-4">{tt(T.compare.headers.cap, lang)}</div>
          <div className="col-span-4">{tt(T.compare.headers.legacy, lang)}</div>
          <div className="col-span-4 text-[color:var(--reva-signal)]">{tt(T.compare.headers.reva, lang)}</div>
        </div>
        {T.compare.rows.map((row, i) => (
          <div key={i} className="grid grid-cols-12 px-6 py-4 border-b border-white/[0.05] last:border-0 items-center">
            <div className="col-span-4 text-[13px] text-white/75">{tt(row.a, lang)}</div>
            <div className="col-span-4 text-[13px] text-white/55">{tt(row.b, lang)}</div>
            <div className="col-span-4 text-[13px] text-white">{tt(row.c, lang)}</div>
          </div>
        ))}
      </div>
    </Section>
  );
}

function CTABanner({ lang, onDemo }) {
  return (
    <Section className="py-24">
      <div className="reva-glass-2 p-12 relative overflow-hidden">
        <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full pointer-events-none" style={{ background: 'radial-gradient(closest-side, rgba(0,168,107,0.30), transparent 70%)' }} />
        <div className="relative grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8">
            <Eyebrow>{tt(T.cta.eyebrow, lang)}</Eyebrow>
            <h2 className="mt-4 reva-display text-3xl sm:text-5xl max-w-[20ch]">{tt(T.cta.title, lang)}</h2>
            <p className="mt-5 text-white/75 max-w-xl text-[14px] leading-relaxed">{tt(T.cta.sub, lang)}</p>
          </div>
          <div className="md:col-span-4 flex md:justify-end">
            <button onClick={onDemo} className="reva-btn" data-testid="reva-cta-bottom">
              {tt(T.cta.btn, lang)} <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </Section>
  );
}

function FAQ({ lang }) {
  const [open, setOpen] = useState(0);
  return (
    <Section id="faq" className="py-24">
      <Eyebrow>{tt(T.faq.eyebrow, lang)}</Eyebrow>
      <h2 className="mt-5 reva-display text-3xl sm:text-5xl max-w-[20ch]">{tt(T.faq.title, lang)}</h2>
      <div className="mt-10 max-w-3xl space-y-2">
        {T.faq.items.map((it, i) => {
          const isOpen = open === i;
          return (
            <div key={i} className="reva-glass overflow-hidden">
              <button onClick={() => setOpen(isOpen ? -1 : i)} className="w-full text-start px-5 py-4 flex items-center justify-between gap-4" data-testid={`reva-faq-${i}`}>
                <span className="text-[15px] text-white">{tt(it.q, lang)}</span>
                {isOpen ? <Minus className="h-4 w-4 text-white/70 shrink-0" /> : <Plus className="h-4 w-4 text-white/70 shrink-0" />}
              </button>
              {isOpen && <div className="px-5 pb-5 text-[14px] text-white/75 leading-relaxed">{tt(it.a, lang)}</div>}
            </div>
          );
        })}
      </div>
    </Section>
  );
}

function Footer({ lang, navigate }) {
  const handleClick = (to) => {
    if (!to) return;
    if (to.startsWith('mailto:')) { window.location.href = to; return; }
    if (to.startsWith('#'))       { document.querySelector(to)?.scrollIntoView({ behavior: 'smooth' }); return; }
    navigate(to);
  };
  return (
    <footer className="relative z-10 mt-12 bg-[#0B1220] text-white px-6 md:px-10 py-14" data-testid="reva-footer">
      <div className="max-w-[1240px] mx-auto grid md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <RevaLogo variant="full" size={32} theme="dark" />
          <p className="mt-5 text-[13px] text-white/65 leading-relaxed max-w-sm">{tt(T.footer.tag, lang)}</p>
        </div>
        {T.footer.cols.map((c, i) => (
          <div key={i} className="md:col-span-2">
            <div className="text-[11px] reva-mono uppercase tracking-[0.18em] text-white/55">{tt(c.h, lang)}</div>
            <ul className="mt-4 space-y-2.5">
              {c.l.map((x, j) => (
                <li key={j}>
                  <button onClick={() => handleClick(x.to)} className="text-[13px] text-white/75 hover:text-white transition-colors text-start" data-testid={`reva-footer-${i}-${j}`}>
                    {tt(x.t, lang)}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <div className="max-w-[1240px] mx-auto mt-12 pt-6 border-t border-white/[0.10] flex flex-wrap items-center justify-between gap-3 text-[11px] reva-mono text-white/55">
        <div>{tt(T.footer.rights, lang)}</div>
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/privacy')} className="hover:text-white transition-colors uppercase tracking-[0.16em]" data-testid="footer-privacy-link">
            {lang === 'ar' ? 'سياسة الخصوصية' : 'Privacy'}
          </button>
          <button onClick={() => navigate('/terms')} className="hover:text-white transition-colors uppercase tracking-[0.16em]" data-testid="footer-terms-link">
            {lang === 'ar' ? 'شروط الخدمة' : 'Terms'}
          </button>
          <span className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[color:var(--reva-mint)] reva-pulse" />{tt(T.footer.nominal, lang)}</span>
        </div>
      </div>
    </footer>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────────────────────────
export default function Landing() {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const [lang, setLang] = useState(() => (typeof window !== 'undefined' && localStorage.getItem('reva_lang')) || i18n.language || 'en');
  const [demoOpen, setDemoOpen] = useState(false);
  const [tourOpen, setTourOpen] = useState(false);

  // Keep i18n + html dir in sync with our local lang state, and persist
  useEffect(() => {
    try { localStorage.setItem('reva_lang', lang); } catch { /* ignore */ }
    if (i18n && typeof i18n.changeLanguage === 'function') i18n.changeLanguage(lang);
    document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = lang;
    document.documentElement.style.background = '#FFFFFF';
    return () => { document.documentElement.style.background = ''; };
  }, [lang, i18n]);

  return (
    <div className="reva-landing min-h-screen" dir={lang === 'ar' ? 'rtl' : 'ltr'} data-testid="reva-landing">
      <TopNavMega lang={lang} setLang={setLang} onDemo={() => setDemoOpen(true)} onTour={() => setTourOpen(true)} />
      {/* Real-estate-first hero — light, architectural, calm */}
      <Hero lang={lang} onDemo={() => setDemoOpen(true)} onTour={() => setTourOpen(true)} />
      {/* Property operations modules — six tightly-built modules */}
      <OperationsModules lang={lang} navigate={navigate} />
      {/* Live operational pulse — keep, recontextualized as portfolio telemetry */}
      <LivePulseBar lang={lang} />
      {/* Operational intelligence panel — AI as a quiet co-pilot */}
      <OperationalIntelligence lang={lang} onDemo={() => setDemoOpen(true)} />
      {/* Enterprise trust */}
      <Security lang={lang} />
      <CTABanner lang={lang} onDemo={() => setDemoOpen(true)} />
      <FAQ lang={lang} />
      <Footer lang={lang} navigate={navigate} />
      <DemoRequestModal open={demoOpen} onClose={() => setDemoOpen(false)} lang={lang} />
      <TourModal open={tourOpen} onClose={() => setTourOpen(false)} lang={lang} navigate={navigate} />
    </div>
  );
}
